import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Home from "@/pages/Home";
import NewsList from "@/pages/NewsList";
import NewsDetail from "@/pages/NewsDetail";
import Informationen from "@/pages/Informationen";
import FAQ from "@/pages/FAQ";
import Legal from "@/pages/Legal";
import PublicLayout from "@/components/PublicLayout";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/components/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminHomepage from "@/pages/admin/AdminHomepage";
import AdminNews from "@/pages/admin/AdminNews";
import AdminNewsEdit from "@/pages/admin/AdminNewsEdit";
import AdminFaqs from "@/pages/admin/AdminFaqs";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminNewsletterCompose from "@/pages/admin/AdminNewsletterCompose";
import AdminLegal from "@/pages/admin/AdminLegal";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminPartners from "@/pages/admin/AdminPartners";
import AdminTermine from "@/pages/admin/AdminTermine";
import AdminSperrungen from "@/pages/admin/AdminSperrungen";
import AdminMaintenance from "@/pages/admin/AdminMaintenance";

import Kontakt from "@/pages/Kontakt";
import Partner from "@/pages/Partner";
import Liveverkehr from "@/pages/Liveverkehr";
import Cookies from "@/pages/Cookies";
import NewsletterConfirm from "@/pages/NewsletterConfirm";
import BerufsverkehrTicker from "@/pages/BerufsverkehrTicker";
import BerufsverkehrTickerConfirm from "@/pages/BerufsverkehrTickerConfirm";
import BerufsverkehrTickerVerwalten from "@/pages/BerufsverkehrTickerVerwalten";
import AdminCommuter from "@/pages/admin/AdminCommuter";
import VoiceNrwLanding from "@/pages/standalone/VoiceNrwLanding";
import AdminTomTom from "@/pages/admin/AdminTomTom";
import AdminBauchroniken from "@/pages/admin/AdminBauchroniken";
import AdminBauchronikEdit from "@/pages/admin/AdminBauchronikEdit";
import Bauchroniken from "@/pages/Bauchroniken";
import BauchronikDetail from "@/pages/BauchronikDetail";
import ScreenNewsHorizontal from "@/pages/screens/ScreenNewsHorizontal";
import ScreenNewsVertical from "@/pages/screens/ScreenNewsVertical";

import "@/App.css";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-sm text-neutral-500" data-testid="auth-loading">Lade…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-center" richColors closeButton />
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/news" element={<NewsList />} />
            <Route path="/news/:slug" element={<NewsDetail />} />
            <Route path="/informationen" element={<Informationen />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/kontakt" element={<Kontakt />} />
            <Route path="/partner" element={<Partner />} />
            <Route path="/liveverkehr" element={<Liveverkehr />} />
            <Route path="/impressum" element={<Legal slug="impressum" />} />
            <Route path="/datenschutz" element={<Legal slug="datenschutz" />} />
            <Route path="/barrierefreiheit" element={<Legal slug="barrierefreiheit" />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/newsletter/bestaetigt" element={<NewsletterConfirm />} />
            <Route path="/berufsverkehr-ticker" element={<BerufsverkehrTicker />} />
            <Route path="/berufsverkehr-ticker/bestaetigt" element={<BerufsverkehrTickerConfirm />} />
            <Route path="/berufsverkehr-ticker/verwalten/:token" element={<BerufsverkehrTickerVerwalten />} />
            <Route path="/bauchroniken" element={<Bauchroniken />} />
            <Route path="/bauchroniken/:slug" element={<BauchronikDetail />} />
          </Route>

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<Protected><AdminLayout /></Protected>}>
            <Route index element={<AdminDashboard />} />
            <Route path="startseite" element={<AdminHomepage />} />
            <Route path="news" element={<AdminNews />} />
            <Route path="news/new" element={<AdminNewsEdit />} />
            <Route path="news/:id" element={<AdminNewsEdit />} />
            <Route path="termine" element={<AdminTermine />} />
            <Route path="sperrungen" element={<AdminSperrungen />} />
            <Route path="faqs" element={<AdminFaqs />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
            <Route path="newsletter/versenden" element={<AdminNewsletterCompose />} />
            <Route path="berufsverkehr-ticker" element={<AdminCommuter />} />
            <Route path="tomtom" element={<AdminTomTom />} />
            <Route path="bauchroniken" element={<AdminBauchroniken />} />
            <Route path="bauchroniken/new" element={<AdminBauchronikEdit />} />
            <Route path="bauchroniken/:id" element={<AdminBauchronikEdit />} />
            <Route path="contacts" element={<AdminContacts />} />
            <Route path="partners" element={<AdminPartners />} />
            <Route path="legal" element={<AdminLegal />} />
            <Route path="wartung" element={<AdminMaintenance />} />
          </Route>

          {/* Public-Display Screens (nicht verlinkt, password-protected) */}
          <Route path="/screen-news-horizontal" element={<ScreenNewsHorizontal />} />
          <Route path="/screen-news-vertical" element={<ScreenNewsVertical />} />

          {/* Standalone SEO-Landingpages — NICHT in Navigation, NICHT im Footer.
              Nur über direkte URL erreichbar. */}
          <Route path="/mailboxansage-bestellen" element={<VoiceNrwLanding />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
